import React, { createContext, useContext, useState, useCallback, useRef, useEffect } from 'react';
import { CheckCircle, AlertCircle, Info, X, AlertTriangle, RefreshCw } from 'lucide-react';
import useDialog, { setDialogDiscardConfirm } from '../hooks/useDialog';

// ============================================================================
// Shared in-app toast + confirm system. Replaces native alert() and window.confirm().
// Wrap the app in <UIProvider>. Then from any component:
//   const showToast = useToast();
//   showToast('success', 'Patient registered');
//   showToast('error', 'Failed to save');
//
//   const confirm = useConfirm();
//   const ok = await confirm({ title: 'Delete?', message: '...', confirmText: 'Delete', tone: 'danger' });
//   if (!ok) return;
// ============================================================================

const ToastContext = createContext(() => {});
const ConfirmContext = createContext(() => Promise.resolve(false));
const PromptContext = createContext(() => Promise.resolve(null));

export const useToast = () => useContext(ToastContext);
export const useConfirm = () => useContext(ConfirmContext);
export const usePrompt = () => useContext(PromptContext);

// ============ Toast ============
const toastStyles = {
  success: { icon: CheckCircle, iconClass: 'text-emerald-500',   border: 'border-emerald-200', bg: 'bg-emerald-50',   text: 'text-emerald-900' },
  error:   { icon: AlertCircle, iconClass: 'text-red-500',       border: 'border-red-200',     bg: 'bg-red-50',       text: 'text-red-900' },
  info:    { icon: Info,        iconClass: 'text-blue-500',      border: 'border-blue-200',    bg: 'bg-blue-50',      text: 'text-blue-900' },
  warning: { icon: AlertTriangle, iconClass: 'text-amber-500',   border: 'border-amber-200',   bg: 'bg-amber-50',     text: 'text-amber-900' },
};

const Toast = ({ toast, onDismiss }) => {
  const style = toastStyles[toast.type] || toastStyles.info;
  const Icon = style.icon;
  return (
    <div className={`flex items-start gap-3 px-4 py-3 rounded-xl border ${style.border} ${style.bg} shadow-lg animate-slide-in-right pointer-events-auto min-w-[280px] max-w-sm`}>
      <Icon className={`w-5 h-5 mt-0.5 flex-shrink-0 ${style.iconClass}`} />
      {/* min-w-0 + break-words: backend exception messages often contain long
          unbreakable tokens (FQCNs, stack frames). Without these, the flex child's
          intrinsic width pushes past max-w-sm and the text spills outside the
          rounded border. */}
      <div className={`flex-1 min-w-0 text-sm font-medium break-words ${style.text}`}>{toast.message}</div>
      <button
        aria-label="Dismiss"
        onClick={() => onDismiss(toast.id)}
        className={`flex-shrink-0 p-0.5 rounded hover:bg-black/5 ${style.text} opacity-60 hover:opacity-100`}
      >
        <X className="w-4 h-4" />
      </button>
    </div>
  );
};

// ============ Confirm Dialog ============
const toneStyles = {
  danger:  { icon: AlertTriangle, iconBg: 'bg-red-100',     iconColor: 'text-red-600',     confirmBtn: 'bg-red-600 hover:bg-red-700' },
  warning: { icon: AlertTriangle, iconBg: 'bg-amber-100',   iconColor: 'text-amber-600',   confirmBtn: 'bg-amber-600 hover:bg-amber-700' },
  info:    { icon: Info,          iconBg: 'bg-blue-100',    iconColor: 'text-blue-600',    confirmBtn: 'bg-blue-600 hover:bg-blue-700' },
  primary: { icon: Info,          iconBg: 'bg-emerald-100', iconColor: 'text-emerald-600', confirmBtn: 'bg-emerald-600 hover:bg-emerald-700' },
};

const ConfirmDialog = ({ options, onResolve }) => {
  const [working, setWorking] = useState(false);
  const tone = toneStyles[options.tone] || toneStyles.primary;
  const Icon = tone.icon;

  const handleConfirm = async () => {
    setWorking(true);
    onResolve(true);
  };

  const handleCancel = () => {
    if (working) return;
    onResolve(false);
  };

  // Escape cancels. Focus starts on Cancel when the question is destructive or cautionary, so a
  // reflex Enter never deletes or discards anything, and on the confirm button otherwise.
  const cancelRef = useRef(null);
  const confirmRef = useRef(null);
  const cautious = options.tone === 'danger' || options.tone === 'warning';
  const dialogRef = useDialog(handleCancel, {
    confirmDiscard: false,
    initialFocus: cautious ? cancelRef : confirmRef,
  });

  return (
    <div
      ref={dialogRef}
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-[9999] p-4"
      onClick={(e) => { if (e.target === e.currentTarget) handleCancel(); }}
    >
      <div className="bg-white rounded-2xl shadow-2xl max-w-sm w-full p-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className={`w-12 h-12 ${tone.iconBg} rounded-xl flex items-center justify-center flex-shrink-0`}>
            <Icon className={`h-6 w-6 ${tone.iconColor}`} />
          </div>
          <div className="min-w-0">
            <h3 className="font-bold text-gray-900">{options.title || 'Confirm'}</h3>
            {options.subtitle && <p className="text-sm text-gray-500 truncate">{options.subtitle}</p>}
          </div>
        </div>
        {options.message && (
          <div className="text-gray-700 mb-6 text-sm">{options.message}</div>
        )}
        <div className="flex space-x-3">
          <button
            ref={cancelRef}
            onClick={handleCancel}
            disabled={working}
            className="flex-1 py-2.5 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-100 font-medium transition-colors disabled:opacity-50"
          >
            {options.cancelText || 'Cancel'}
          </button>
          <button
            ref={confirmRef}
            onClick={handleConfirm}
            disabled={working}
            className={`flex-1 py-2.5 text-white rounded-xl font-medium transition-colors disabled:opacity-60 flex items-center justify-center gap-2 ${tone.confirmBtn}`}
          >
            {working && <RefreshCw className="w-4 h-4 animate-spin" />}
            <span>{options.confirmText || 'Confirm'}</span>
          </button>
        </div>
      </div>
    </div>
  );
};

// ============ Prompt Dialog ============
const PromptDialog = ({ options, onResolve }) => {
  const [value, setValue] = useState(options.defaultValue || '');
  const [error, setError] = useState('');
  const tone = toneStyles[options.tone] || toneStyles.primary;
  const Icon = tone.icon;

  const handleSubmit = (e) => {
    e?.preventDefault?.();
    const trimmed = (value || '').trim();
    if (options.required && !trimmed) {
      setError(options.errorMessage || 'This field is required');
      return;
    }
    onResolve(trimmed);
  };

  const dialogRef = useDialog(() => onResolve(null), { confirmDiscard: false });

  return (
    <div
      ref={dialogRef}
      className="fixed inset-0 bg-black/60 flex items-center justify-center z-[9999] p-4"
      onClick={(e) => { if (e.target === e.currentTarget) onResolve(null); }}
    >
      <form onSubmit={handleSubmit} className="bg-white rounded-2xl shadow-2xl max-w-md w-full p-6">
        <div className="flex items-center space-x-3 mb-4">
          <div className={`w-12 h-12 ${tone.iconBg} rounded-xl flex items-center justify-center flex-shrink-0`}>
            <Icon className={`h-6 w-6 ${tone.iconColor}`} />
          </div>
          <div className="min-w-0">
            <h3 className="font-bold text-gray-900">{options.title || 'Input Required'}</h3>
            {options.subtitle && <p className="text-sm text-gray-500 truncate">{options.subtitle}</p>}
          </div>
        </div>
        {options.message && (
          <div className="text-gray-700 mb-3 text-sm">{options.message}</div>
        )}
        {options.multiline ? (
          <textarea
            autoFocus
            rows={3}
            value={value}
            onChange={(e) => { setValue(e.target.value); if (error) setError(''); }}
            placeholder={options.placeholder || ''}
            className={`w-full p-3 border-2 rounded-xl resize-none focus:ring-2 focus:ring-emerald-500 ${error ? 'border-red-300 bg-red-50' : 'border-gray-200 focus:border-emerald-500'}`}
          />
        ) : (
          <input
            autoFocus
            type="text"
            value={value}
            onChange={(e) => { setValue(e.target.value); if (error) setError(''); }}
            placeholder={options.placeholder || ''}
            className={`w-full p-3 border-2 rounded-xl focus:ring-2 focus:ring-emerald-500 ${error ? 'border-red-300 bg-red-50' : 'border-gray-200 focus:border-emerald-500'}`}
          />
        )}
        {error && <p className="text-red-500 text-xs mt-1">{error}</p>}
        <div className="flex space-x-3 mt-5">
          <button
            type="button"
            onClick={() => onResolve(null)}
            className="flex-1 py-2.5 border border-gray-300 text-gray-700 rounded-xl hover:bg-gray-100 font-medium transition-colors"
          >
            {options.cancelText || 'Cancel'}
          </button>
          <button
            type="submit"
            className={`flex-1 py-2.5 text-white rounded-xl font-medium transition-colors ${tone.confirmBtn}`}
          >
            {options.confirmText || 'Submit'}
          </button>
        </div>
      </form>
    </div>
  );
};

// ============ Provider ============
export const UIProvider = ({ children }) => {
  const [toasts, setToasts] = useState([]);
  const [confirmState, setConfirmState] = useState(null); // { options, resolve }
  const [promptState, setPromptState] = useState(null);   // { options, resolve }
  const nextId = useRef(1);

  const dismissToast = useCallback((id) => {
    setToasts(prev => prev.filter(t => t.id !== id));
  }, []);

  const showToast = useCallback((type, message, duration = 4000) => {
    const id = nextId.current++;
    setToasts(prev => [...prev, { id, type, message }]);
    if (duration > 0) {
      setTimeout(() => dismissToast(id), duration);
    }
    return id;
  }, [dismissToast]);

  // Listen for 401-triggered session expiry from api.js. We surface a single sticky toast
  // (duration=0) so the user can't miss it — without dedupe, 5 concurrent in-flight 401s
  // would stack 5 identical toasts and then disappear together when one is dismissed.
  // Tagged with a 'tag' field so we can find and replace it idempotently. The companion
  // 'auth:login' listener clears the toast once the user has logged back in successfully.
  useEffect(() => {
    const onLogout = () => {
      setToasts(prev => {
        if (prev.some(t => t.tag === 'session-expired')) return prev;
        return [
          ...prev,
          {
            id: nextId.current++,
            type: 'error',
            tag: 'session-expired',
            message: 'Session expired. Please log in again.',
          },
        ];
      });
    };
    const onLogin = () => {
      setToasts(prev => prev.filter(t => t.tag !== 'session-expired'));
    };
    window.addEventListener('auth:logout', onLogout);
    window.addEventListener('auth:login', onLogin);
    return () => {
      window.removeEventListener('auth:logout', onLogout);
      window.removeEventListener('auth:login', onLogin);
    };
  }, []);

  const confirm = useCallback((options = {}) => {
    return new Promise((resolve) => {
      setConfirmState({ options, resolve });
    });
  }, []);

  // Dialogs ask through this before Escape throws away what was typed into them.
  useEffect(() => {
    setDialogDiscardConfirm(confirm);
    return () => setDialogDiscardConfirm(null);
  }, [confirm]);

  const handleConfirmResolve = useCallback((result) => {
    if (confirmState) {
      confirmState.resolve(result);
      setConfirmState(null);
    }
  }, [confirmState]);

  const prompt = useCallback((options = {}) => {
    return new Promise((resolve) => {
      setPromptState({ options, resolve });
    });
  }, []);

  const handlePromptResolve = useCallback((result) => {
    if (promptState) {
      promptState.resolve(result);
      setPromptState(null);
    }
  }, [promptState]);

  return (
    <ToastContext.Provider value={showToast}>
      <ConfirmContext.Provider value={confirm}>
        <PromptContext.Provider value={prompt}>
          {children}

          {/* Toast stack — top right */}
          <div className="fixed top-5 right-5 z-[9998] flex flex-col gap-2 pointer-events-none">
            {toasts.map(t => (
              <Toast key={t.id} toast={t} onDismiss={dismissToast} />
            ))}
          </div>

          {/* Confirm dialog */}
          {confirmState && (
            <ConfirmDialog options={confirmState.options} onResolve={handleConfirmResolve} />
          )}

          {/* Prompt dialog */}
          {promptState && (
            <PromptDialog options={promptState.options} onResolve={handlePromptResolve} />
          )}
        </PromptContext.Provider>
      </ConfirmContext.Provider>
    </ToastContext.Provider>
  );
};
